import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Asset, AssetAppeal, Report } from "types";
import { useTooltip } from "context/TooltipProvider";
import { AssetAppealDailyReport } from "components/reports/AssetAppealReport/DailyReport";
import { AssetAppealFinalReport } from "components/reports/AssetAppealReport/FinalReport";

export const AssetAppealReportTabs: React.FC<{
  appealId?: string;
  assets?: Asset;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: () => void;
  appeal: AssetAppeal | null;
  assetsNeedingReport: Asset[];
  setAppeal?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
  reports?: Report[];
}> = ({
  appealId,
  assets,
  onNewDataAdded,
  setActiveTab,
  appeal,
  assetsNeedingReport,
  setAppeal,
  reports,
}) => {
  const { t } = useTranslation();
  const [activeReportTab, setActiveReportTab] = useState<"daily" | "final">(
    "daily",
  );
  const [isFinalVerdict, setIsFinalVerdict] = useState(false);
  const { showTooltip } = useTooltip();

  const hasDailyReports = () => {
    const dailyProhibitionReports = reports?.filter(
      (report) => report.is_next_appointment_reason_final_verdict === false,
    );

    return (dailyProhibitionReports?.length ?? 0) > 0;
  };

  const handleIsFinalVerdictSwitch = () => {
    if (!hasDailyReports()) {
      showTooltip(t("add_daily_report_before_final"), "info");
      return;
    }
    setIsFinalVerdict(!isFinalVerdict);
    if (!isFinalVerdict) {
      setActiveReportTab("final");
    } else {
      setActiveReportTab("daily");
    }
  };

  return (
    <div className="w-[50%] bg-white shadow-md rounded-b-lg rounded-r-lg pb-4 pt-2 mt-2">
      {/* Tab Headers */}
      <div className="flex items-center justify-between mb-2 mt-3 ml-2 pr-4">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveReportTab("daily")}
            disabled={isFinalVerdict}
            className={`px-4 rounded-t-lg transition-colors ${
              activeReportTab === "daily"
                ? "referral-active-tab"
                : isFinalVerdict
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                  : "referral-inactive-tab hover:bg-gray-100 bg-gray-200 text-gray-400"
            }`}
          >
            {t("add_report")}
          </button>

          <button
            onClick={() => setActiveReportTab("final")}
            disabled={!isFinalVerdict}
            className={`px-4 rounded-t-lg transition-colors ${
              activeReportTab === "final"
                ? "referral-active-tab"
                : !isFinalVerdict
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                  : "referral-inactive-tab hover:bg-gray-100 bg-gray-200 text-gray-400 "
            }`}
          >
            {t("add_final_report")}
          </button>
        </div>

        {/* Final Verdict Switch */}
        <div className="flex items-center space-x-2 relative ml-2">
          <span className="text-sm text-gray-600">{t("is_final_verdict")}</span>
          <button
            type="button"
            onClick={handleIsFinalVerdictSwitch}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isFinalVerdict ? "bg-red-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isFinalVerdict ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-1">
        {activeReportTab === "daily" ? (
          <AssetAppealDailyReport
            appealId={appealId}
            setActiveTab={setActiveTab}
            onNewDataAdded={onNewDataAdded}
            appeal={appeal}
            assets={assets as Asset}
            setAppeal={setAppeal}
          />
        ) : (
          <AssetAppealFinalReport
            appealId={appealId}
            setActiveTab={setActiveTab}
            onNewDataAdded={onNewDataAdded}
            appeal={appeal}
            assets={appeal?.asset as Asset}
            assetsNeedingReport={assetsNeedingReport}
            setAppeal={setAppeal}
          />
        )}
      </div>
    </div>
  );
};
